import React, { useState } from "react";
import {
  TextField,
  InputAdornment,
  Button,
  TextFieldProps,
  Box,
  Typography,
  SxProps,
  Theme,
  Divider,
} from "@mui/material";
import * as AiIcons from "react-icons/ai";
import colors from "../theme/Colours";

interface CustomInputFieldProps
  extends Omit<TextFieldProps, "variant" | "label"> {
  label: string;
  icon?: React.ReactNode;
  placeholder?: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  sx?: SxProps<Theme>;
  inputSx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
  error?: boolean;
  helperText?: React.ReactNode;
}

// Custom Input Field
export const CustomInputField: React.FC<CustomInputFieldProps> = ({
  label,
  placeholder,
  icon,
  value,
  type = "text",
  onChange,
  sx,
  labelSx,
  inputSx,
  error,
  helperText,
  ...rest
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordField = type === "password";
  const handleToggleVisibility = () => setShowPassword((prev) => !prev);

  return (
    <Box mb={2}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, mb: "8px", ...labelSx }}
      >
        {label}
      </Typography>
      <TextField
        fullWidth
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        error={error}
        helperText={helperText}
        type={isPasswordField ? (showPassword ? "text" : "password") : type}
        InputProps={{
          startAdornment: icon ? (
            <InputAdornment position="start">{icon}</InputAdornment>
          ) : undefined,
          endAdornment: isPasswordField ? (
            <InputAdornment position="end">
              <Box
                component="span"
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={handleToggleVisibility}
                data-testid="toggle-visibility"
              >
                {showPassword ? (
                  <>{AiIcons.AiOutlineEyeInvisible({ size: 20 })}</>
                ) : (
                  <>{AiIcons.AiOutlineEye({ size: 20 })}</>
                )}
              </Box>
            </InputAdornment>
          ) : undefined,
          sx: {
            borderRadius: "10px",
            height: "50px",
            ...inputSx,
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
          },
          "& input::-ms-reveal": { display: "none" },
          "& input::-ms-clear": { display: "none" },
          ...sx,
        }}
        {...rest}
      />
    </Box>
  );
};

// Button
interface CustomButtonProps {
  label: string;
  icon?: React.ReactNode;
  iconPosition?: "start" | "end";
  onClick?: () => void;
  variant?: "contained" | "outlined";
  fullWidth?: boolean;
  type?: "button" | "submit";
  sx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
}

export const CustomButton: React.FC<CustomButtonProps> = ({
  label,
  icon,
  iconPosition,
  onClick,
  variant = "contained",
  fullWidth = true,
  type = "button",
  sx,
  labelSx,
}) => {
  const isOutlined = variant === "outlined";

  return (
    <Button
      variant={variant}
      fullWidth={fullWidth}
      onClick={onClick}
      type={type}
      sx={{
        borderRadius: "8px",
        py: 1.5,
        justifyContent: "center",
        textTransform: "none",
        backgroundColor: isOutlined
          ? "transparent"
          : colors.primaryButtonOrange,
        color: isOutlined ? colors.primaryButtonOrange : colors.background2,
        borderColor: isOutlined ? colors.primaryButtonOrange : undefined,
        "&:hover": {
          backgroundColor: isOutlined
            ? colors.buttonHoverWhite
            : colors.primaryButtonOrangeHover,
        },
        ...sx,
      }}
    >
      <Box display="flex" alignItems="center" gap={1} sx={{ ...labelSx }}>
        {iconPosition === "start" && icon}
        <Box component="span" sx={{ ...labelSx }}>
          {label}
        </Box>
        {iconPosition === "end" && icon}
      </Box>{" "}
    </Button>
  );
};

// OR Divider
export const OrDivider: React.FC = () => (
  <Box display="flex" alignItems="center" width="100%" my={3}>
    <Divider sx={{ flex: 1, borderColor: colors.black }} />
    <Typography sx={{ px: 2, color: "text.secondary", fontSize: "14px" }}>
      OR
    </Typography>
    <Divider sx={{ flex: 1, borderColor: colors.black }} />
  </Box>
);
